import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { PageHeader } from "@/components/common";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Warehouse, CreditCard, CheckCircle2 } from "lucide-react";

export default function AgentSettings() {
  const [wh, setWh] = useState({ name: "", contact_name: "", phone: "", address: "", city: "", state: "", pincode: "" });
  const [rz, setRz] = useState({ key_id: "", key_secret: "" });
  const [rzStatus, setRzStatus] = useState(null);
  const [busy, setBusy] = useState("");

  const load = () => api.get("/agent/settings").then(({ data }) => {
    if (data.warehouse) setWh({ name: data.warehouse.name || "", contact_name: data.warehouse.contact_name || "",
      phone: data.warehouse.phone || "", address: data.warehouse.address || "", city: data.warehouse.city || "",
      state: data.warehouse.state || "", pincode: data.warehouse.pincode || "" });
    setRzStatus(data.razorpay);
  }).catch(() => {});
  useEffect(() => { load(); }, []);

  const saveWarehouse = async () => {
    setBusy("wh");
    try {
      await api.put("/agent/settings/warehouse", wh);
      toast.success("Pickup warehouse saved — live courier rates will use this pincode");
    } catch (e) { toast.error(errMsg(e)); }
    setBusy("");
  };

  const saveRazorpay = async () => {
    setBusy("rz");
    try {
      await api.put("/agent/settings/razorpay", rz);
      toast.success("Razorpay connected — your store can now accept online payments");
      setRz({ key_id: "", key_secret: "" });
      load();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy("");
  };

  const wf = (key, label, testid) => (
    <div>
      <Label className="text-xs text-slate-500">{label}</Label>
      <Input data-testid={testid} value={wh[key]} onChange={(e) => setWh({ ...wh, [key]: e.target.value })} className="mt-1" />
    </div>
  );

  return (
    <div className="space-y-5" data-testid="agent-settings">
      <PageHeader title="Settings" sub="Pickup warehouse (courier rates) and your payment gateway." testid="settings-title" />

      <Card className="border-slate-200">
        <CardContent className="space-y-3 p-5">
          <div className="flex items-center gap-2">
            <Warehouse size={16} className="text-orange-500" />
            <p className="text-sm font-bold text-slate-800">Pickup Warehouse</p>
          </div>
          <p className="text-[11px] leading-relaxed text-slate-500">
            Couriers collect your orders from this address. It drives the exact live shipping rate your customers see at checkout
            (warehouse PIN → customer PIN × product weight). Set at registration — update anytime here.
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            {wf("name", "Warehouse / business name *", "wh-name")}
            {wf("contact_name", "Contact person", "wh-contact")}
            {wf("phone", "Phone", "wh-phone")}
            {wf("pincode", "PIN code * (6 digits)", "wh-pincode")}
            {wf("city", "City", "wh-city")}
            {wf("state", "State", "wh-state")}
          </div>
          {wf("address", "Full address", "wh-address")}
          <Button data-testid="warehouse-save-btn" onClick={saveWarehouse} disabled={busy === "wh"}
            className="bg-orange-500 text-white hover:bg-orange-600">
            {busy === "wh" ? "Saving…" : "Save warehouse"}
          </Button>
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardContent className="space-y-3 p-5">
          <div className="flex items-center gap-2">
            <CreditCard size={16} className="text-orange-500" />
            <p className="text-sm font-bold text-slate-800">Razorpay Payment Gateway</p>
            {rzStatus?.configured && (
              <span className="flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700" data-testid="razorpay-configured-badge">
                <CheckCircle2 size={11} /> Connected ({rzStatus.key_id})
              </span>
            )}
          </div>
          <p className="text-[11px] leading-relaxed text-slate-500">
            Connect your own Razorpay account so online payments (UPI/cards/netbanking) on your store settle <b>directly into your account</b>.
            Find these in Razorpay Dashboard → Settings → API Keys. Your secret is stored encrypted and never shown again.
            Until you connect, your store accepts <b>Cash on Delivery only</b>.
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label className="text-xs text-slate-500">Key ID *</Label>
              <Input data-testid="razorpay-key-id" value={rz.key_id} onChange={(e) => setRz({ ...rz, key_id: e.target.value })}
                placeholder="rzp_live_…" className="mt-1" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Key Secret *</Label>
              <Input data-testid="razorpay-key-secret" type="password" value={rz.key_secret} onChange={(e) => setRz({ ...rz, key_secret: e.target.value })}
                placeholder="••••••••" className="mt-1" />
            </div>
          </div>
          <Button data-testid="razorpay-save-btn" onClick={saveRazorpay} disabled={busy === "rz"}
            className="bg-orange-500 text-white hover:bg-orange-600">
            {busy === "rz" ? "Connecting…" : rzStatus?.configured ? "Update Razorpay keys" : "Connect Razorpay"}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
